import React, { useEffect, useState, useMemo, FC } from "react";
import ReactHtmlParser from "react-html-parser";
import keyBy from "lodash/keyBy";
import kebabCase from "lodash/kebabCase";
import isUndefined from "lodash/isUndefined";
import isEmpty from "lodash/isEmpty";

import { useLazyQuery, useQuery } from "@stadium/services/apollo-client";
import { useJuneSoComponentContext } from "@stadium/services/june-analytics";
import { useModal } from "@stadium/hooks/lib/use-modal";

import { fetchMenu } from "@graphql/queries/stores/menu/main";
import { fetchWidgetStoreDetails } from "@graphql/queries/stores/widget-store-details";

import { BootstrapTab, BootstrapTabs } from "@stadium/ui/src/components/tabs";
import Multimedia from "@sharedComponents/multimedia";
import ShimmerWrapper from "@stadium/ui/src/components/loaders/shimmer-wrapper";
import { ExternalButton, InternalButton } from "@sharedComponents/buttons";
import ProductDetailsModal from "@components/store/product-details-modal";

import useFetchBaseProductList from "@hooks/search/use-fetch-base-product-list";

import { getShopUrl } from "@stadium/helpers/store";
import { getLockerShopESQueryVars } from "@helpers/store";
import { getAllMenuTaxons, getNestedOrderedMenuItems } from "@helpers/menu";
import { appendCatalogParamToUrl, getPreviewCatalogModalDetail } from "@helpers/catalog";

import { MENU_INTENT } from "@constants/menu";
import { PRODUCT_DETAILS_MODAL } from "@constants/modal-types";
import { CATALOG_SLUG } from "@stadium/constants/shop";
import NoImage from "@assets/images/no-image.svg";
import { DEFAULT_COUNTRY_ISO } from "@stadium/constants";

import defaultStyles from "./main.module.scss";
interface DefaultObjType {
  [key: string]: any;
}

type FeaturedShopSectionProps = {
  data: any;
  content: {
    buttonText: string;
    brandImgs?: {
      src: string;
      alt: string;
    }[];
  };
  storeNumber: string;
  checkForSwag?: boolean;
  styles?: ModularStyles;
  linkToCatalogShop: boolean;
};

const defaultObj: DefaultObjType = {};

const defaultArr: any[] = [];

const DEFAULT_CURRENT_PAGE = 1;

const FeaturedShopSection: FC<FeaturedShopSectionProps> = ({
  data,
  content,
  storeNumber,
  checkForSwag,
  linkToCatalogShop = true,
  styles = defaultStyles,
}) => {
  const { buttonText, brandImgs = defaultArr } = content || defaultObj;
  const { jsonContent } = data || defaultObj;
  const contentData = (jsonContent && JSON.parse(jsonContent)) || defaultObj;
  const { rte_content = "", background_color } = contentData || defaultObj;
  const { componentName } = useJuneSoComponentContext();
  const [productDetailData, setProductDetailData] = useState(defaultObj);
  const {
    openModal: openProductDetailsModalCatalog,
    renderModal: renderProductDetailsModalCatalog,
  } = useModal(PRODUCT_DETAILS_MODAL, ProductDetailsModal);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryTabs, setCategoryTabs] = useState(defaultArr);
  const {
    fetchedProducts,
    getProductList,
    loading: isProductListLoading,
  } = useFetchBaseProductList();

  const categoryTabMapping = useMemo(
    () => keyBy(categoryTabs, (category) => category?.code),
    [categoryTabs],
  );

  const [fetchStore, { data: storeData, loading: isStoreDataLoading }] = useLazyQuery(
    fetchWidgetStoreDetails,
    {
      variables: {
        number: storeNumber,
      },
      fetchPolicy: "cache-and-network",
    },
  );

  const variables = {
    location: "eshop-main",
    intent: MENU_INTENT,
    storeNumber,
    globalZone: false,
    country: DEFAULT_COUNTRY_ISO,
    menuItemsCountry: DEFAULT_COUNTRY_ISO,
  };

  const { data: menuData } = useQuery(fetchMenu, {
    variables,
    fetchPolicy: "cache-and-network",
  });
  const { menuItems = defaultArr, id: menuId } = menuData?.menu || defaultObj;

  const fetchedOrderedMenuItem = useMemo(() => {
    return getNestedOrderedMenuItems({ menuItems });
  }, [menuItems]);

  const {
    parentId,
    syncedToEs,
    cloneId,
    cacheKey,
    isLockerShop,
    enabledGlobalCatalog,
    config,
    id: storeId,
    feUrl: storeUrl,
    number,
  } = storeData?.store || defaultObj;

  const { allMenuTaxonIds: taxonIds, anyMenuWithSwag: withSwag } = useMemo(
    () => getAllMenuTaxons([selectedCategory, ...(selectedCategory?.children || defaultArr)]),
    [selectedCategory],
  );

  const storeLink = useMemo(
    () =>
      linkToCatalogShop
        ? appendCatalogParamToUrl(`${getShopUrl({ slug: CATALOG_SLUG, shopNumber: number })}/menu`)
        : storeUrl,
    [number, linkToCatalogShop, storeUrl],
  );

  const queryVariables = useMemo(() => {
    const variables = {
      menuId: Number(menuId),
      menuItemId: Number(selectedCategory?.id),
      storeId: Number(parentId),
      taxonIds,
      page: DEFAULT_CURRENT_PAGE,
      perPage: 4,
      skipWarehouse: !syncedToEs && !!cloneId,
      sortBy: "recommended",
      withSwag,
      storeUpdatedAt: cacheKey,
      optimizedSearch: isLockerShop,
      showVariantsAsProducts: true,
      enabledGlobalCatalog,
      storeUpcharge: config?.upcharge,
      globalZone: false,
      countryCode: "us",
      ...getLockerShopESQueryVars({
        isLockerShop,
      }),
      ecoFriendly: false,
      budget: 0,
      bulkProducts: false,
      noMinQuantity: false,
      designedStoreIds: [Number(storeId)],
      skipAggregations: true,
    };
    return variables;
  }, [
    menuId,
    selectedCategory?.id,
    parentId,
    taxonIds,
    syncedToEs,
    cloneId,
    withSwag,
    cacheKey,
    isLockerShop,
    enabledGlobalCatalog,
    config?.upcharge,
    storeId,
  ]);

  useEffect(() => {
    fetchStore();
  }, []);
  useEffect(() => {
    if (!selectedCategory && fetchedOrderedMenuItem) {
      const sectionTabs = fetchedOrderedMenuItem?.children.filter(
        ({ linkedTaxonResources, forSwag }) => {
          if (checkForSwag) {
            return !isUndefined(forSwag) && !forSwag;
          }
          return !isEmpty(linkedTaxonResources);
        },
      );
      setCategoryTabs(sectionTabs);
      setSelectedCategory(sectionTabs[0]);
    }
  }, [checkForSwag, fetchedOrderedMenuItem, menuItems, selectedCategory]);

  useEffect(() => {
    if (selectedCategory && storeData) {
      getProductList({
        variables: queryVariables,
      });
    }
  }, [selectedCategory, getProductList, menuId, queryVariables, storeData]);

  const renderShimmerLoader = () => (
    <ShimmerWrapper
      length={4}
      wrapperClass={styles.tabShimmerContainer}
      cardClass={styles.shimmerCard}
    >
      <div className={styles.tabShimmer}></div>
    </ShimmerWrapper>
  );

  const renderTabContent = () =>
    isProductListLoading ? (
      <div className={styles.productListShimmer}>{renderShimmerLoader()}</div>
    ) : (
      fetchedProducts.map((data) => {
        const { baseImage, name, brand, id, variantImages } = data || defaultObj;

        const productImage = baseImage || variantImages?.[0];

        return (
          <InternalButton
            styleType="no-style"
            onClick={() => {
              openProductDetailsModalCatalog();
              setProductDetailData(data);
            }}
            key={id}
            styles={styles}
            className={styles.tabContent}
            title={name}
          >
            <Multimedia
              src={productImage || NoImage}
              alt={name}
              type="image"
              isNextImage={false}
              className={styles.tabContentItem}
            />
            <div className={styles.imageDetails} aria-hidden="true">
              <div className={styles.brandDetail}>
                <h5 className={styles.brandName}>{brand.displayName}</h5>
                <div className={styles.brandDes}>{name}</div>
              </div>
            </div>
          </InternalButton>
        );
      })
    );

  const renderCategoryNavItems = () =>
    categoryTabs.map((category) => (
      <BootstrapTab
        key={category?.code}
        eventKey={category?.code}
        className={styles.tabContentContainer}
        title={category?.name}
      >
        {selectedCategory?.code === category?.code ? renderTabContent() : ""}
      </BootstrapTab>
    ));

  const renderSectionContent = () => (
    <BootstrapTabs
      id={`catalog-section-${storeNumber}`}
      activeKey={selectedCategory?.code}
      onSelect={(key) => setSelectedCategory(categoryTabMapping[key])}
      className={styles.navTabs}
    >
      {renderCategoryNavItems()}
    </BootstrapTabs>
  );

  return (
    <div
      className={styles.exploreContainer}
      {...(background_color && { style: { backgroundColor: background_color } })}
    >
      <div className={styles.exploreHeader}>{rte_content && ReactHtmlParser(rte_content)}</div>
      <div className={styles.exploreContent}>
        {selectedCategory ? (
          renderSectionContent()
        ) : (
          <div className={styles.shimmerSection}>{renderShimmerLoader()}</div>
        )}
        {buttonText && selectedCategory && (
          <ExternalButton
            href={storeUrl ? storeLink : ""}
            styleType="primary"
            isLoading={isStoreDataLoading}
            className={styles.browseButton}
            showExternalLinkIcon
            analytics={{
              componentName: componentName ? `${componentName}-featured-section` : "",
              target: kebabCase(buttonText),
            }}
            target={linkToCatalogShop ? "" : "_blank"}
            title={buttonText}
            tab-index="0"
          >
            {buttonText}
          </ExternalButton>
        )}
      </div>
      {brandImgs.length ? (
        <ul className={styles.logoListContainer}>
          {brandImgs.map(({ src, alt }) => (
            <li key={alt} className={styles.logoListItem}>
              <Multimedia
                src={src}
                type="image"
                isNextImage={false}
                className={styles.logo}
                alt={alt}
                tab-index="0"
              />
            </li>
          ))}
        </ul>
      ) : (
        ""
      )}
      {renderProductDetailsModalCatalog({
        classes: {
          rootClass: styles.productDetailsCatalogPrimaryModal,
          dialogClass: `${styles.modalDialogCatalogProductDetails} ${styles.revampDesign}`,
        },
        data: {
          ...getPreviewCatalogModalDetail({ ...productDetailData, storeNumber }),
          classes: {
            productImgClass: styles.catalogModeImgSection,
            productContentClass: styles.catalogModeContentClass,
          },
        },
      })}
    </div>
  );
};

export default FeaturedShopSection;
